import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, TargetIcon, MessageSquareIcon, BarChart3Icon, StarIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent as track } from '../../utils/analytics';
import { ctaCopy, ctaOutcomes, type Bi } from '../../data/communicationsFinalCta';
import { CitizenPhone, BrossardDashboard } from './proof/ProofDevices';
import { CityNetwork, IntelligenceCard, MultichannelCard, ReachCard, AtnaRecommendation, FloatingNotification, BrandSignature } from './cta/CtaCards';
const EASE = [0.23, 1, 0.32, 1] as const;
const OUTCOME_ICONS = {
  informed: TargetIcon,
  calls: MessageSquareIcon,
  ops: BarChart3Icon,
  community: StarIcon
} as const;
export function CommunicationsFinalCTA() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const keywords = isFR ? ctaCopy.keywords.fr : ctaCopy.keywords.en;
  return <div className="relative max-w-[1640px] mx-auto" data-id="element-2346" data-mp="2x0tq92nb39kg">
      <div className="pointer-events-none absolute right-0 top-0 w-[620px] h-[620px] rounded-full bg-[#EEEDFD] opacity-70 blur-[140px]" aria-hidden="true" data-id="element-2347" data-mp="2s31x65axdheo" />

      <div className="relative grid grid-cols-1 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-10 xl:gap-6 items-center" data-id="element-2348" data-mp="2nar15djb2fre">
        {/* Left: message */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} className="xl:pl-4" data-id="element-2349" data-mp="2ec6n4poowu9i">
          <p className="font-sans text-[13px] font-semibold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-2350" data-mp="24dppxaz5rjf0">{pick(ctaCopy.eyebrow)}</p>
          <h2 className="font-serif text-[2.6rem] sm:text-[3.6rem] lg:text-[4.3rem] leading-[1.02] tracking-tight mt-6" data-id="element-2351" data-mp="2m4ercvkp4gq">
            <span className="block text-[#121A3A]" data-id="element-2352" data-mp="3ts5qlnnd423f">{pick(ctaCopy.line1)}</span>
            <span className="block text-[#4F46E5]" data-id="element-2353" data-mp="1s3i1smlbauot">{pick(ctaCopy.line2)}</span>
            <span className="block text-[#4F46E5]" data-id="element-2354" data-mp="1s1f1n9qg3l6d">{pick(ctaCopy.line3)}</span>
          </h2>
          <p className="font-sans text-[17px] md:text-[19px] text-[#5B6478] leading-relaxed mt-7 max-w-[640px]" data-id="element-2355" data-mp="1ww1ezig37nt3">{pick(ctaCopy.body)}</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3.5" data-id="element-2356" data-mp="1n3lv2fl8cp5k">
            <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
            location: 'Communications Final CTA'
          })} className="inline-flex items-center justify-center gap-3 rounded-xl bg-[#4F46E5] px-7 py-4 font-sans text-[17px] font-medium text-white shadow-[0_14px_30px_-14px_rgba(79,70,229,0.8)] transition-colors duration-150 hover:bg-[#4338CA] whitespace-nowrap" data-id="element-2357" data-mp="1n1smuvuyxwvg">
              {pick(ctaCopy.primary)}
              <ArrowRightIcon className="w-5 h-5" strokeWidth={2} aria-hidden="true" data-id="element-2358" data-mp="qcb9dupqv6j7" />
            </Link>
            <Link to="/request-demo" onClick={() => track('Contact CTA Clicked', {
            location: 'Communications Final CTA'
          })} className="inline-flex items-center justify-center gap-3 rounded-xl border border-[#E2E3EE] bg-white px-7 py-4 font-sans text-[17px] font-medium text-[#121A3A] transition-colors duration-150 hover:border-[#C9C5F5] whitespace-nowrap" data-id="element-2359" data-mp="uh14hhckwh32">
              {pick(ctaCopy.secondary)}
              <ArrowRightIcon className="w-5 h-5" strokeWidth={2} aria-hidden="true" data-id="element-2360" data-mp="44xpvlw944mz" />
            </Link>
          </div>
          <p className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-1 font-sans text-[15px] text-[#6B7180]" data-id="element-2361" data-mp="3vcth6vgjm26d">
            {keywords.map((k, i) => <Fragment key={k} data-id="element-2362" data-mp="dx3jt162cw4t" data-mpi={i}>
                {i > 0 && <span aria-hidden="true" data-id="element-2363" data-mp="93ir9on8oud0">·</span>}
                <span data-id="element-2364" data-mp="92phsfwwbqr6">{k}</span>
              </Fragment>)}
          </p>
        </motion.div>

        {/* Right: desktop composition */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.3,
        delay: 0.06,
        ease: EASE
      }} className="hidden xl:block relative h-[760px]" data-id="element-2365" data-mp="3ck7oul4ugtqj">
          <CityNetwork isFR={isFR} className="absolute left-0 right-0 top-0 h-[560px]" data-id="element-2366" data-mp="2oubrseyl4on7" />
          <div className="absolute left-[4%] top-[9%] w-[270px]" data-id="element-2367" data-mp="2tltxthfgtff1">
            <IntelligenceCard isFR={isFR} kind="notice" data-id="element-2368" data-mp="2tn50fdx6ch4k" />
          </div>
          <div className="absolute right-0 top-[7%] w-[290px]" data-id="element-2369" data-mp="2yjvgw2cw5eqi">
            <IntelligenceCard isFR={isFR} kind="audience" data-id="element-2370" data-mp="33ior9lwvspmg" />
          </div>
          <div className="absolute left-0 top-[29%] w-[320px]" data-id="element-2371" data-mp="37lbcv52yleb7">
            <MultichannelCard isFR={isFR} data-id="element-2372" data-mp="37lutmigvhwnn" />
          </div>
          <div className="absolute right-0 top-[25%] w-[260px]" data-id="element-2373" data-mp="3ckek8w6evwmc">
            <ReachCard isFR={isFR} data-id="element-2374" data-mp="1d722owqxpu21" />
          </div>
          <div className="absolute right-0 top-[43%] w-[290px]" data-id="element-2375" data-mp="1d8c15n1p9497">
            <AtnaRecommendation isFR={isFR} data-id="element-2376" data-mp="1i5lnuuxpf1tc" />
          </div>
          <div className="absolute left-[18%] bottom-0 z-20" data-id="element-2377" data-mp="1i87wyz08tpdo">
            <CitizenPhone isFR={isFR} showNotice={false} data-id="element-2378" data-mp="1annrls4j9n82" />
          </div>
          <div className="absolute right-0 bottom-0 w-[560px] z-10" data-id="element-2379" data-mp="15vdvfuctrqxc">
            <BrossardDashboard isFR={isFR} data-id="element-2380" data-mp="15u2rnujdqj68" />
          </div>
          <div className="absolute left-[39%] bottom-[9%] z-30" data-id="element-2381" data-mp="10xkt5cen7h2c">
            <FloatingNotification isFR={isFR} data-id="element-2382" data-mp="vy86arnru9zr" />
          </div>
        </motion.div>

        {/* Right: tablet / mobile stack */}
        <div className="xl:hidden space-y-4" data-id="element-2383" data-mp="1tk5k4br7hr5d">
          <CityNetwork isFR={isFR} className="h-[340px] sm:h-[420px]" data-id="element-2384" data-mp="1omdsliuh471p" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-2385" data-mp="1on6otpvt3e5l">
            <IntelligenceCard isFR={isFR} kind="notice" data-id="element-2386" data-mp="1jm9ajodx349d" />
            <IntelligenceCard isFR={isFR} kind="audience" data-id="element-2387" data-mp="1flpdslx9a2nr" />
            <MultichannelCard isFR={isFR} data-id="element-2388" data-mp="1fl5tiyk4v26k" />
            <ReachCard isFR={isFR} data-id="element-2389" data-mp="885hqlj2fm5w" />
            <div className="md:col-span-2" data-id="element-2390" data-mp="896r8lkfo0li">
              <AtnaRecommendation isFR={isFR} data-id="element-2391" data-mp="85sq1gni9xfn" />
            </div>
          </div>
          <div className="pt-4 flex flex-col md:flex-row items-center md:items-end justify-center gap-5" data-id="element-2392" data-mp="458sibz0srsz">
            <CitizenPhone isFR={isFR} data-id="element-2393" data-mp="5lmmha2t08zm" />
            <div className="w-full max-w-[560px]" data-id="element-2394" data-mp="anbzgv9l6wqc">
              <BrossardDashboard isFR={isFR} data-id="element-2395" data-mp="am0uwt8bhky6" />
            </div>
          </div>
        </div>
      </div>

      {/* Outcomes */}
      <ul className="relative mt-12 pt-8 border-t border-[#EEEEF4] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0 lg:divide-x divide-[#EEEEF4]" data-id="element-2396" data-mp="omgt09v9891">
        {ctaOutcomes.map(o => {
        const Icon = OUTCOME_ICONS[o.key];
        return <li key={o.key} className="flex items-start gap-4 lg:px-8" data-id="element-2397" data-mp="3nuvhcqkvnlmo">
              <span className="w-16 h-16 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2398" data-mp="3957jy0fth748">
                <Icon className="w-7 h-7 text-[#4F46E5]" strokeWidth={1.6} data-id="element-2399" data-mp="3e3sizwjc1kr3" />
              </span>
              <span className="min-w-0 pt-1" data-id="element-2400" data-mp="3049klvgbpnrz">
                <span className="block font-sans text-[16.5px] font-semibold text-[#121A3A]" data-id="element-2401" data-mp="3453auvh8neo1">{pick(o.title)}</span>
                <span className="block font-sans text-[14.5px] text-[#5B6478] leading-snug mt-1" data-id="element-2402" data-mp="2lg9b3c2196m1">{pick(o.body)}</span>
              </span>
            </li>;
      })}
      </ul>

      <div className="relative mt-12" data-id="element-2403" data-mp="2uhh53hxinlab">
        <BrandSignature isFR={isFR} data-id="element-2404" data-mp="2ujagfaj2qton" />
      </div>
    </div>;
}